import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DropdownMenu from './DropdownMenu';

const MENU_WIDTH = 240;

/**
 * A menu behind any trigger: `trigger(toggle, isOpen)` draws the button, the
 * admin's DropdownMenu opens under it in <body> (no scrolling column clips
 * it), aligned on its right edge (`align="right"`) or its left one. A click
 * outside or on an item closes it; `items` are those of DropdownMenu.
 */
const MenuButton = ({ trigger, items, align = 'right' }) => {
  const [position, setPosition] = useState(null);
  const box = useRef(null);
  const menu = useRef(null);

  useEffect(() => {
    if (!position) return undefined;
    const close = (event) => {
      if (box.current?.contains(event.target) || menu.current?.contains(event.target)) return;
      setPosition(null);
    };
    const hide = () => setPosition(null);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', hide, true);
    };
  }, [position]);

  const toggle = (event) => {
    event?.stopPropagation();
    if (position) return setPosition(null);
    const rect = box.current.getBoundingClientRect();
    const left = align === 'right' ? rect.right - MENU_WIDTH : rect.left;
    return setPosition({ top: rect.bottom + 4, left: Math.max(8, Math.min(left, window.innerWidth - MENU_WIDTH - 8)) });
  };

  return (
    <>
      <span ref={box} className="inline-flex">
        {trigger(toggle, Boolean(position))}
      </span>
      {position &&
        createPortal(
          <div ref={menu} role="menu" className="fixed z-[9999]" style={position}>
            <DropdownMenu
              width="w-60"
              items={items.filter(Boolean).map((item) =>
                item.separator
                  ? item
                  : {
                      ...item,
                      onClick: () => {
                        setPosition(null);
                        item.onClick();
                      },
                    },
              )}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

export default MenuButton;
